import React from 'react';
import { Card, Row, Col } from 'react-bootstrap';
import { BloodworkAnalysis, PanelMarker, FurtherConsiderations, Section } from '../../models';
import { AnemiaStatus } from './bloodwork-anemia-status';
import { Footer } from '../../Footer';
import { TabsComponent } from '../tabs-component';
import { WBCEvaluation } from './bloodwork-wbc-evaluation';
import { GlucoseRegulationInsights } from './glucose-regulation-insights';
import { ThyroidRegulationInsights } from './thyroid-regulation-insights';
import { StressAdrenalRegulationInsights } from './stress-adrenal-regulation-insights';
import { DehydrationInsights, DEHYDRATION_PANEL_ENABLED } from './dehydration-insights';
import { adrenalGauge } from '../../models/StressAdrenalAnalysis';
import { BloodworkEntryStore } from '../../stores/bloodwork-entry-store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SectionVideoIcon } from '../common/section-video-icon';

type Props = {
  section: Section;
  panelTitle: string;
  bloodworkAnalysis: BloodworkAnalysis;
  panelMarkers: PanelMarker[];
  furtherConsiderations: FurtherConsiderations;
  evalSmall: number;
  refSmall: number;
  evalLarge: number;
  refLarge: number;
  infoLink: string;
  bloodworkEntryStore: BloodworkEntryStore;
  enabledSupplementBrands: string[];
  collapsed: boolean;
  onToggle: () => void;
  includeInPdf: boolean;
  onIncludeInPdfToggle: () => void;
  // Optional extra checkboxes rendered in the header to the LEFT of the
  // "Include in PDF" checkbox (e.g. the Immune Activity section's quick
  // "Only Show Section Signals" / "Show Absolutes" view toggles). Listed
  // left-to-right in array order.
  extraToggles?: Array<{
    id: string;
    label: string;
    checked: boolean;
    onToggle: () => void;
    title?: string;
  }>;
};

// LAYOUT: the insights panel and the tabs must NEVER overlap. When there isn't
// room for both, they stack instead.
//
// Both halves of this row are fixed-width panels, so a percentage split can't
// hold them:
//   - the insights panel is a fixed 300px (.evaluationPanel), needing 330px
//     with the column's padding;
//   - the Reference Range tab's bar-graph panel is a fixed 760px
//     (.ref-range-analysis-panel), needing 790px.
// A percentage column narrower than its fixed-width child does not shrink the
// child — the child spills out of the column and lands on whatever is beside
// it. With the old 3/9 split the insights column only reached 330px once the
// window passed ~1360px, so below that the 300px panel hung over into the tabs
// column and the tab strip and Results panel drew on top of it.
//
// So the split is by CONTENT, not percentage:
//   - insights column is `xl="auto"` — exactly as wide as its 300px panel, at
//     every window size, so it can never overflow;
//   - tabs column is `xl` (flex-grow) — it takes all remaining width, which
//     also puts the leftover space into the tabs rather than stranding it.
// Below `xl` (1200px) both are xs={12} and simply stack: insights panel on
// top, tabs and Results panel below, each full width. 1200px is the point
// where 330 + 790 = 1120px of fixed panels still fits side by side.
//
// The evalLarge / refLarge props (the old 3/9 spans) are consequently unused
// here — kept on Props because every caller still passes them.
export class BloodworkTestAnalysisSection extends React.Component<Props, {}> {

  evaluation = () => {
    const { bloodworkAnalysis, section, evalSmall } = this.props;
    if (section === Section.RBC) {
      return (
        <Col xs={evalSmall} xl="auto">
          <AnemiaStatus bloodworkAnalysis={bloodworkAnalysis} />
        </Col>  
      );
    } else if (section === Section.WBC && bloodworkAnalysis.wbc.hasValue()) {
      return (
        <Col xs={evalSmall} xl="auto">
          <WBCEvaluation bloodworkAnalysis={bloodworkAnalysis} />
        </Col>
      );
    } else if (section === Section.GLUCOSE) {
      // When the insights panel renders nothing (e.g. amylase is the only
      // glucose-section marker), skip the eval Col entirely so the Results
      // panel left-aligns instead of sitting behind an empty left gap.
      if (!GlucoseRegulationInsights.shouldRender(bloodworkAnalysis)) {
        return null;
      }
      return (
        <Col xs={evalSmall} xl="auto">
          <GlucoseRegulationInsights bloodworkAnalysis={bloodworkAnalysis} />
        </Col>
      );
    } else if (
      section === Section.ELECTROLYTES &&
      // Hidden in every shipped build — see DEHYDRATION_PANEL_ENABLED. It is a
      // single exported flag rather than the condition repeated here because
      // AnemiaStatus reads the SAME flag to decide whether to keep rendering
      // the dehydration line: one constant means the panel and the RBC line
      // can never both show, or both vanish.
      DEHYDRATION_PANEL_ENABLED
    ) {
      // Hydration gauge. Needs at least one of RBC/HGB/HCT to say anything, so
      // skip the eval Col entirely when there's no hemoconcentration panel —
      // same left-align behaviour as the Glucose and Adrenal sections.
      if (!DehydrationInsights.shouldRender(bloodworkAnalysis)) {
        return null;
      }
      return (
        <Col xs={evalSmall} xl="auto">
          <DehydrationInsights bloodworkAnalysis={bloodworkAnalysis} />
        </Col>
      );
    } else if (section === Section.THYROID && process.env.REACT_APP_ENV !== 'prod' && process.env.REACT_APP_ENV !== 'slava') {
      return (
        <Col xs={evalSmall} xl="auto">
          <ThyroidRegulationInsights bloodworkAnalysis={bloodworkAnalysis} />
        </Col>
      );
    } else if (section === Section.STRESS_ADRENAL && process.env.REACT_APP_ENV !== 'prod' && process.env.REACT_APP_ENV !== 'slava') {
      // With no Na/K ratio the insights gauge renders nothing (see
      // StressAdrenalRegulationInsights). Skip the eval Col entirely so the
      // Results panel left-aligns like sections without an insights panel,
      // instead of sitting in the right column behind an empty left gap.
      if (!adrenalGauge(bloodworkAnalysis.sodiumPotassiumRatio)) {
        return null;
      }
      return (
        <Col xs={evalSmall} xl="auto">
          <StressAdrenalRegulationInsights bloodworkAnalysis={bloodworkAnalysis} />
        </Col>
      );
    } else {
      return null;
    }
  }

  // `fullWidth` when this section has no left-hand insights/evaluation panel —
  // the tabs then take the whole card rather than leaving the missing column's
  // width as dead space on the right, which squeezed wide tab content (Notes,
  // its digest cards, Root Causes) for no reason.
  analysis = (fullWidth: boolean) => {
    const { section, bloodworkAnalysis, panelMarkers, furtherConsiderations,
      refSmall, bloodworkEntryStore, infoLink, enabledSupplementBrands } = this.props;
      return (
        <Col xs={fullWidth ? 12 : refSmall} xl={fullWidth ? 12 : true}>
          <TabsComponent
            section={section}
            panelMarkers={panelMarkers}
            bloodworkAnalysis={bloodworkAnalysis} 
            furtherConsiderations={furtherConsiderations}
            bloodworkEntryStore={bloodworkEntryStore}
            infoLink={infoLink}
            enabledSupplementBrands={enabledSupplementBrands}
          />
        </Col>
      );
  }

  render() {
    const { panelTitle, panelMarkers, collapsed, onToggle, section, includeInPdf, onIncludeInPdfToggle, extraToggles } = this.props;
    const chevron = collapsed ? 'chevron-down' : 'chevron-up';
    // Built once so the tabs column knows whether the left column is there.
    const evaluationCol = this.evaluation();
    return (
      <>
      { panelMarkers.length > 0 ?
        <Card>
          <Card.Header
            onClick={onToggle}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <span>
              <FontAwesomeIcon icon={chevron} />&nbsp;&nbsp;{panelTitle}
              <SectionVideoIcon section={section} />
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 16 }}>
              {(extraToggles ?? []).map(toggle => (
                <label
                  key={toggle.id}
                  htmlFor={toggle.id}
                  onClick={e => e.stopPropagation()}
                  style={{ display: 'inline-flex', alignItems: 'center', margin: 0, fontWeight: 'normal', cursor: 'pointer', color: '#fff' }}
                  title={toggle.title}
                >
                  <span style={{ marginRight: '6px' }}>{toggle.label}</span>
                  <input
                    id={toggle.id}
                    type="checkbox"
                    className="lsm-styled-checkbox"
                    checked={toggle.checked}
                    onChange={toggle.onToggle}
                  />
                </label>
              ))}
              <label
                htmlFor={`include-in-pdf-${section}`}
                onClick={e => e.stopPropagation()}
                style={{ display: 'inline-flex', alignItems: 'center', margin: 0, fontWeight: 'normal', cursor: 'pointer', color: '#fff' }}
                title="Include this section in the Analysis PDF"
              >
                <span style={{ marginRight: '6px' }}>Include in PDF</span>
                <input
                  id={`include-in-pdf-${section}`}
                  type="checkbox"
                  className="lsm-styled-checkbox"
                  checked={includeInPdf}
                  onChange={onIncludeInPdfToggle}
                />
              </label>
            </span>
          </Card.Header>
          {!collapsed && (
            <>
              <Card.Body>
                <Row>
                  {evaluationCol}
                  {this.analysis(!evaluationCol)}
                </Row>
              </Card.Body>
              <Card.Footer>
                <Footer />
              </Card.Footer>
            </>
          )}
        </Card>
        : null
      }
      </>
    );
  }
}
